<template>
    <div>
        <div class="mb-4 flex items-center justify-between space-x-2">
            <h1 class="text-lg font-semibold text-gray-700">Verify</h1>
        </div>

        <div class="p-8 space-y-8 bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
            <div class="flex items-center gap-x-3 p-4 rounded-lg text-sm text-red-600 bg-red-50 border border-red-200">
                <Icon name="i-ph-warning-duotone" class="size-4" />
                <span>Use this tool only in the case where OTP codes are failing to be delivered to a new user.</span>
            </div>
            
            <UForm ref="form" @submit="onSubmit" :state="state" :schema="schema" :validate-on="['submit']" class="space-y-4">
                <UFormGroup name="phone" label="Phone number" required>
                    <InputPhone v-model="state.phone" size="xl" />
                </UFormGroup>

                <UButton type="submit" :disabled="!state.phone" size="lg" :loading="loading" color="black"> Verify </UButton>
            </UForm>
        </div>
    </div>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { phoneDigits, phoneSchema } from '~/utils/phone'
import type { FormSubmitEvent } from '#ui/types'
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Settings – Verify' })

const toast = useToast()

const loading = ref(false)
const state = ref({
    phone: ''
})

const schema = z.object({
    phone: phoneSchema({ required: true }),
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    try {
        const password = await $fetch('/api/settings/verify', {
            method: 'POST',
            body: { phone: phoneDigits(event.data.phone) }
        })

        toast.add({
            title: `User verified!\n\nPhone: ${event.data.phone}\nPassword: ${password}\n`,
            description: `Please copy the temporary password, as it won't be visible anymore once closed.`,
            color: 'green',
            closeButton: { ui: { base: '!hidden' } } as never,
            icon: 'i-ph-check-circle-duotone',
            timeout: 0,
            actions: [{ label: 'Copy password and close', click: () => {
                useClipboard().copy(password)
            }}],
            ui: {
                title: 'whitespace-pre-wrap',
                description: '!mt-4'
            }
        })

        state.value.phone = ''
    } catch (e: any) {
        if (e.statusCode === 403) {
            toast.add({
                title: 'User already verified.',
                color: 'red',
                icon: 'i-ph-warning-circle-duotone'
            })
        }
        else {
            toast.add({
                title: 'Invalid user.',
                color: 'red',
                icon: 'i-ph-warning-circle-duotone'
            })
        }
    } finally {
        loading.value = false
    }
}
</script>
